import { useEffect, useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articlesApi, type ArticleDetail, type ArticleUsageRow } from "../api/articles";
import type { ApiError } from "../api/client";
import { detailsMap } from "../lib/apiError";
import { formatDateTime } from "../lib/dates";
import { formatQuantityWithUnit } from "../lib/quantity";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { usePrompt } from "../components/ConfirmDialog";
import { AttributeValuesView } from "../components/AttributeValuesView";
import { ArticleFormModal } from "../components/ArticleFormModal";
import { HistoryPanel } from "../components/HistoryPanel";
import { PermLink } from "../components/PermLink";
import { PositionKindIcon } from "../components/PositionKindIcon";
import { TextPopover } from "../components/TextPopover";
import Ic from "../components/icons.jsx";

/** `/artikli/:id` — the article card (sprint 5l §7): base data, attributes, "Gde se koristi", history. */
export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const prompt = usePrompt();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("articles:edit");
  const canDelete = hasPermission("articles:delete");

  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [usage, setUsage] = useState<ArticleUsageRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    articlesApi.get(id)
      .then((d) => { if (!cancelled) setArticle(d); })
      .catch((err: ApiError) => { if (!cancelled) setError(err.status === 404 ? t("articles.notFound", "Artikal ne postoji.") : err.message); });
    return () => { cancelled = true; };
  }, [id, t]);

  // "Gde se koristi" is skipped when the card already says the article is on no position (hard rule 11).
  const positionCount = article?.positionCount ?? 0;
  useEffect(() => {
    if (!id || !article) return;
    if (positionCount === 0) { setUsage([]); return; }
    let cancelled = false;
    articlesApi.usage(id)
      .then((rows) => { if (!cancelled) setUsage(rows); })
      .catch((err: ApiError) => { if (!cancelled) { setUsage([]); toast.error(err.message); } });
    return () => { cancelled = true; };
    // `article` identity changes with every save; the count is what decides the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, positionCount, article !== null]);

  async function handleDelete() {
    if (!article) return;
    const reason = await prompt({
      title: t("articles.confirmDelete.title", "Brisanje artikla"),
      message: t("articles.confirmDelete.message", "Obrisati artikal „{{code}} — {{name}}“?", { code: article.code, name: article.name }),
      confirmLabel: t("action.delete", "Obriši"),
      placeholder: t("deleteReason.placeholder", "Razlog brisanja (obavezno) — npr. rashodovano, zamenjeno, greška pri unosu…"),
      multiline: true,
      required: true,
      danger: true,
    });
    if (!reason) return;
    try {
      await articlesApi.remove(article.id, reason);
      toast.success(t("articles.toast.deleted", "Artikal obrisan."));
      navigate("/artikli", { replace: true });
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "IN_USE") {
        const d = detailsMap(ae);
        const count = Number(d?.positionCount ?? article.positionCount);
        const positions = (d?.positions as unknown as { code: string | null; name: string }[] | undefined) ?? [];
        const names = positions.map((p) => (p.code ? `${p.code} ${p.name}` : p.name)).join(", ");
        toast.error(names
          ? t("articles.error.inUseNamed", "Artikal je na sastavnici {{count}} pozicija ({{positions}}) — prvo ga uklonite sa njih.", { count, positions: names })
          : t("articles.error.inUse", "Artikal je na sastavnici {{count}} pozicija — prvo ga uklonite sa njih.", { count }));
      } else {
        toast.error(ae.message);
      }
    }
  }

  if (error) {
    return (
      <div>
        <div className="page-head"><div><h1>{t("articles.karton", "Karton artikla")}</h1></div></div>
        <div className="card"><div style={errorBox} role="alert">{error}</div></div>
      </div>
    );
  }
  if (!article) {
    return <div className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></div>;
  }

  // The article cannot go while a sastavnica carries it — the button says so instead of waiting for the 409.
  const deleteReason = article.positionCount > 0
    ? t("articles.error.inUse", "Artikal je na sastavnici {{count}} pozicija — prvo ga uklonite sa njih.", { count: article.positionCount })
    : null;
  const colPath = t("articles.usage.col.path", "Putanja");
  const colPosition = t("articles.usage.col.position", "Pozicija");
  const colQuantity = t("articles.usage.col.quantity", "Količina");
  const colNote = t("articles.usage.col.note", "Napomena");
  const dash = <span className="muted">—</span>;

  return (
    <div>
      <nav className="crumbs" aria-label="Breadcrumb">
        <span><Link to="/artikli">{t("articles.title", "Artikli")}</Link></span>
        <span>{article.code}</span>
      </nav>

      {/* Header: code, name, type / unit / manufacturer, actions */}
      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-h" style={{ alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 260px" }}>
            <h1 style={{ margin: 0, fontSize: 22, fontFamily: "var(--mono)", letterSpacing: "-0.01em", overflowWrap: "anywhere" }}>{article.code}</h1>
            <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2, overflowWrap: "anywhere" }}>{article.name}</div>
            <div className="sub" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              <PermLink permission="article-types:view" to={`/tipovi/artikli/${article.typeId}`} className="tag" style={{ textDecoration: "none" }}>
                <span className="mono" style={{ fontSize: 11.5 }}>{article.typeCode}</span> {article.typeName}
              </PermLink>
              <span className="tag neutral" title={t("articles.field.unit", "Jedinica mere")}>{article.unit}</span>
              {article.manufacturer && <span className="tag neutral" title={t("articles.field.manufacturer", "Proizvođač")}>{article.manufacturer}</span>}
              <span className="muted">
                {article.positionCount > 0
                  ? t("articles.positionCount", "na {{count}} pozicija", { count: article.positionCount })
                  : t("articles.usageEmpty", "Nije ni na jednoj sastavnici.")}
              </span>
            </div>
          </div>
          {(canEdit || canDelete) && (
            <div className="page-actions" style={{ flex: "none" }}>
              {canEdit && (
                <button type="button" className="btn" onClick={() => setEditing(true)}>
                  <Ic.pencil /> {t("action.edit", "Izmeni")}
                </button>
              )}
              {canDelete && (
                <span className="tip-wrap" data-tip={deleteReason ?? undefined} title={deleteReason ?? undefined}>
                  <button type="button" className="btn" style={dangerBtn} disabled={deleteReason !== null} onClick={handleDelete}>
                    {t("action.delete", "Obriši")}
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
        {article.note && (
          <div className="card-b" style={{ paddingTop: 12, paddingBottom: 12 }}>
            <div className="kv-grid-item">
              <dt style={kvLabel}>{t("articles.field.note", "Napomena")}</dt>
              <dd style={{ margin: "2px 0 0", whiteSpace: "pre-wrap", fontSize: 13.5 }}>{article.note}</dd>
            </div>
          </div>
        )}
      </section>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Attribute values (read-only; "Izmeni" opens the modal) */}
        <section className="card">
          <div className="card-h">
            <div>
              <h3>{t("articles.section.attributes", "Atributi")}</h3>
              <div className="sub">{t("articles.attributesSub", "Vrednosti prema atributima tipa artikla.")}</div>
            </div>
          </div>
          {article.attributeDefs.length === 0 ? (
            <div className="empty">{t("articles.attributesEmpty", "Tip artikla nema definisane atribute.")}</div>
          ) : (
            <div className="card-b">
              <AttributeValuesView defs={article.attributeDefs} values={article.attributeValues} />
            </div>
          )}
        </section>

        {/* Where it is used */}
        <section className="card">
          <div className="card-h">
            <div>
              <h3>{t("articles.section.usage", "Gde se koristi")}</h3>
              <div className="sub">{t("articles.usageSub", "Pozicije na čijoj je sastavnici ovaj artikal, sa količinom.")}</div>
            </div>
          </div>
          {usage === null ? (
            <div className="empty">{t("common.loading", "Učitavanje…")}</div>
          ) : usage.length === 0 ? (
            <div className="empty">{t("articles.usageEmpty", "Nije ni na jednoj sastavnici.")}</div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl list-card">
                <thead>
                  <tr>
                    <th className="hide-tablet">{colPath}</th>
                    <th>{colPosition}</th>
                    <th className="num">{colQuantity}</th>
                    <th className="hide-tablet">{colNote}</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.map((row) => (
                    <tr key={row.positionId}>
                      <td className="hide-tablet" data-label={colPath}>
                        {row.path.length > 0 ? <span className="muted">{row.path.map((p) => p.name).join(" › ")}</span> : dash}
                      </td>
                      <td data-label={colPosition}>
                        <PermLink permission="structure:view" to={`/pozicija/${row.positionId}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <PositionKindIcon kind={row.kind} />
                          <b>{row.name}</b>
                          {row.code && <span className="mono muted" style={{ fontSize: 12 }}>{row.code}</span>}
                        </PermLink>
                      </td>
                      <td className="num" data-label={colQuantity}>{formatQuantityWithUnit(row.quantity, article.unit)}</td>
                      <td className="hide-tablet cell-note" data-label={colNote}>{row.note ? <TextPopover text={row.note} /> : dash}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <HistoryPanel
          entityKind="ARTICLE"
          entityId={article.id}
          title={t("articles.section.history", "Istorija izmena")}
          refreshKey={historyKey}
        />
        <div className="muted" style={{ fontSize: 12 }}>
          {t("articles.stamps", "Kreiran {{created}} · izmenjen {{updated}}", { created: formatDateTime(article.createdAt), updated: formatDateTime(article.updatedAt) })}
        </div>
      </div>

      {editing && (
        <ArticleFormModal
          article={article}
          onClose={() => setEditing(false)}
          onSaved={(saved) => {
            setEditing(false);
            setArticle(saved);
            setHistoryKey((k) => k + 1);
            toast.success(t("articles.toast.saved", "Artikal sačuvan."));
          }}
        />
      )}
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
const kvLabel: CSSProperties = { fontSize: 11, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--ink-3)" };
